import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useGameSocket } from "@/hooks/useGameSocket";
import { HitmasterLogo } from "@/components/HitmasterLogo";
import { VinylReveal } from "@/components/VinylReveal";
import { PlayerScoreCard } from "@/components/PlayerScoreCard";
import { Equalizer } from "@/components/Equalizer";
import { BackToLobby } from "@/components/BackToLobby";
import { usePersistentPlayer } from "@/components/PersistentPlayer";
import { MosaicReveal } from "@/components/MosaicReveal";
import { GameTypeBadge } from "@/components/GameTypeBadge";
import { toast } from "sonner";
import { getTheme, getStoredStyle, bgImageStyle } from "@/lib/themes";
import { playerColor } from "@/lib/playerColors";
import { playTick } from "@/lib/sfx";
import { VideoBg } from "@/components/VideoBg";
import { Save } from "lucide-react";
import { useLang } from "@/i18n";

export default function HostGame() {
  const { code } = useParams();
  const nav = useNavigate();
  const { t: tr } = useLang();
  const player = usePersistentPlayer();
  const [room, setRoom] = useState(null);
  const [truth, setTruth] = useState(null);
  const [verdicts, setVerdicts] = useState({});
  const [buzzerPlayerId, setBuzzerPlayerId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [snippetTimer, setSnippetTimer] = useState(null);
  const [needsUnlock, setNeedsUnlock] = useState(false);
  const [autoNextIn, setAutoNextIn] = useState(null);
  const [paused, setPaused] = useState(false);
  const [mosaicArtwork, setMosaicArtwork] = useState(null);
  const [mosaicOrder, setMosaicOrder] = useState([]);
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [roundStat, setRoundStat] = useState(null);
  const [challengeInfo, setChallengeInfo] = useState(null); // {playerId, artist, track, year, afterPass, endsAt}
  const [challengeLeft, setChallengeLeft] = useState(null);
  const isPlayingRef = useRef(false);
  const customAudioRef = useRef(null);
  const CINEMATIC_AUDIO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_music-quiz-game-2/artifacts/nbhlfq5t_Firefly_audio_5_seconds_rising_tune_cinematic%2C_arp_with_drums_12_variation4.wav";

  // reveal countdown (frozen while the host has paused the sequence)
  useEffect(() => {
    if (autoNextIn == null || autoNextIn <= 0 || paused) return;
    const t = setTimeout(() => setAutoNextIn((n) => (n == null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [autoNextIn, paused]);

  // Challenge-window countdown on the host (mirrors the players' 5s window).
  useEffect(() => {
    const endsAt = challengeInfo?.endsAt;
    if (!endsAt) { setChallengeLeft(null); return; }
    const end = new Date(endsAt).getTime();
    const tick = () => setChallengeLeft(Math.max(0, Math.ceil((end - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 200);
    return () => clearInterval(iv);
  }, [challengeInfo?.endsAt]);

  // Suspense tick on each second of the challenge countdown (louder in the final 2s).
  useEffect(() => {
    if (challengeLeft == null || challengeLeft <= 0) return;
    playTick(challengeLeft <= 2);
  }, [challengeLeft]);

  const unlockAndPlay = () => {
    try { player?.unmute?.(); } catch {}
    try { player?.play?.(); } catch {}
    setNeedsUnlock(false);
  };

  // Load & play the current song via persistent player whenever the round changes
  useEffect(() => {
    if (room?.status !== "in_round" || !room?.currentSong?.videoId || !player) return;
    isPlayingRef.current = false;
    setNeedsUnlock(false);
    // G3 = "Raad de artiest — Photo Mosaic" uses a custom cinematic clip
    // instead of the YouTube audio. Silence the YouTube player and let the
    // custom <audio> element (below) drive the sound.
    if (room?.gameType === 3) {
      try { player.mute(); } catch {}
      try { player.pause(); } catch {}
      // Play custom clip
      try {
        if (customAudioRef.current) {
          customAudioRef.current.currentTime = 0;
          customAudioRef.current.loop = true;
          const p = customAudioRef.current.play();
          if (p && p.catch) p.catch(() => setNeedsUnlock(true));
        }
      } catch {}
      return () => {
        try { if (customAudioRef.current) { customAudioRef.current.pause(); customAudioRef.current.currentTime = 0; } } catch {}
      };
    }
    // G4 = "Videoclip op je telefoon" — audio & video play on each player's phone.
    // The host stays completely silent.
    if (room?.gameType === 4) {
      try { player.mute(); } catch {}
      try { player.pause(); } catch {}
      try { player.stop(); } catch {}
      return;
    }
    // audioSource='players' — music plays on each player's device for G1/G2 too.
    // Load the video into the hidden host player (so we still track playback events)
    // but keep it muted so nothing comes out of the host speaker.
    if (room?.audioSource === "players") {
      try { player.mute(); } catch {}
      try { player.load(room.currentSong.videoId); } catch {}
      return;
    }
    try { player.unmute(); } catch {}
    try { player.load(room.currentSong.videoId); } catch {}
    // watchdog: if not playing after 2.5s, show unlock button
    const t = setTimeout(() => {
      if (!isPlayingRef.current) setNeedsUnlock(true);
    }, 2500);
    return () => clearTimeout(t);
  }, [room?.currentRound, room?.status, room?.currentSong?.videoId, room?.gameType]); // eslint-disable-line

  // Pause the custom G3 clip when someone buzzes or the round is over
  useEffect(() => {
    if (!customAudioRef.current) return;
    const shouldStop = room?.gameType !== 3 || room?.status !== "in_round" || !!buzzerPlayerId;
    if (shouldStop) {
      try { customAudioRef.current.pause(); } catch {}
    }
  }, [room?.gameType, room?.status, buzzerPlayerId]);

  // Track playback state from the persistent player
  useEffect(() => {
    if (!player?.onStateChange) return;
    return player.onStateChange((s) => {
      // 1 = PLAYING, 3 = BUFFERING (both signal playback is starting/live)
      if (s === 1 || s === 3) {
        isPlayingRef.current = true;
        setNeedsUnlock(false);
      }
    });
  }, [player]);

  // Stop playback when leaving the game screen
  useEffect(() => {
    return () => { try { player?.stop?.(); } catch {} };
  }, []); // eslint-disable-line

  // Stop the host music the moment the round is revealed/finished (e.g. solo G2
  // immediate reveal) so the song doesn't keep playing over the answer.
  useEffect(() => {
    if (room?.status === "reveal" || room?.status === "finished") {
      try { player?.pause?.(); } catch {}
      try { if (customAudioRef.current) customAudioRef.current.pause(); } catch {}
    }
  }, [room?.status]); // eslint-disable-line

  const { status } = useGameSocket({
    code,
    playerId: "host",
    role: "host",
    onMessage: (msg) => {
      if (msg.type === "HELLO") {
        setRoom(msg.room);
        if (msg.room?.albumArtwork) setMosaicArtwork(msg.room.albumArtwork);
        if (Array.isArray(msg.room?.mosaicOrder)) setMosaicOrder(msg.room.mosaicOrder);
        const to = msg.room?.turnOrder || [];
        setActivePlayerId(to.length ? to[msg.room?.activeIndex ?? 0] : null);
      }
      if (msg.type === "PLAYER_JOINED") setRoom((r) => ({ ...(r || {}), players: msg.players }));
      if (msg.type === "PLAYER_LEFT") {
        if (Array.isArray(msg.players)) setRoom((r) => ({ ...(r || {}), players: msg.players }));
      }
      if (msg.type === "ROUND_STARTED") {
        setRoundStat(null);
        setRoom((r) => ({ ...(r || {}), status: "in_round", currentRound: msg.round, currentSong: msg.song, playedCount: msg.playedCount, totalSongs: msg.totalSongs, gameType: msg.gameType }));
        setTruth(null);
        setVerdicts({});
        setBuzzerPlayerId(null);
        setAnswers({});
        setAutoNextIn(null);
        setPaused(false);
        setMosaicArtwork(msg.albumArtwork || null);
        setMosaicOrder(msg.mosaicOrder || []);
        setActivePlayerId(msg.activePlayerId ?? null);
        setChallengeInfo(null);
        setChallengeLeft(null);
      }
      if (msg.type === "TURN_UPDATE") { setActivePlayerId(msg.activePlayerId ?? null); setChallengeInfo(null); setChallengeLeft(null); }
      if (msg.type === "BUZZED") setBuzzerPlayerId(msg.playerId);
      if (msg.type === "ANSWER_SUBMITTED") {
        setAnswers((a) => ({ ...a, [msg.playerId]: true }));
        if (msg.answer) setChallengeInfo((c) => ({ ...(c || {}), playerId: msg.playerId, artist: msg.answer.artist, track: msg.answer.track, year: msg.answer.year, afterPass: false }));
      }
      if (msg.type === "CHALLENGE_OPEN") {
        setChallengeInfo((c) => ({
          ...(c || {}),
          afterPass: !!msg.afterPass,
          playerId: msg.afterPass ? msg.passedPlayerId : ((c && c.playerId) || msg.targetPlayerId),
          endsAt: msg.challengeEndsAt || null,
        }));
      }
      if (msg.type === "ALL_PASSED") { setChallengeInfo(null); setChallengeLeft(null); }
      if (msg.type === "REVEAL") {
        setTruth(msg.truth);
        setVerdicts(msg.verdicts || {});
        setRoundStat(msg.roundStat || null);
        setChallengeInfo(null);
        setChallengeLeft(null);
        setRoom((r) => ({ ...(r || {}), status: "reveal", players: msg.players }));
        setPaused(false);
        setAutoNextIn(msg.autoNextInSec ?? null);
      }
      if (msg.type === "PAUSE_STATE") setPaused(!!msg.paused);
      if (msg.type === "GAME_FINISHED") {
        setRoom((r) => ({ ...(r || {}), status: "finished", players: msg.players || r?.players }));
      }
      if (msg.type === "CHALLENGED") {
        toast.info(`Challenge by ${msg.byPlayerId?.slice(0, 6)}…`);
      }
    },
  });

  // initial fetch + short poll so the host self-heals from any missed WS message
  const syncRoom = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${code}`);
      setRoom((r) => ({ ...(r || {}), ...data, gameType: data.currentGameType ?? data.gameType }));
      if (data.albumArtwork) setMosaicArtwork(data.albumArtwork);
      if (Array.isArray(data.mosaicOrder)) setMosaicOrder(data.mosaicOrder);
      const to = data.turnOrder || [];
      setActivePlayerId(to.length ? to[data.activeIndex ?? 0] : null);
      if (data.status === "reveal" && data.reveal) {
        setTruth((prev) => prev || data.reveal.truth || null);
        setVerdicts((prev) => (prev && Object.keys(prev).length ? prev : (data.reveal.verdicts || {})));
      }
      return data;
    } catch (e) {
      return null;
    }
  }, [code]);

  useEffect(() => {
    let cancelled = false;
    syncRoom().then((d) => { if (d === null && !cancelled) nav("/"); });
    const iv = setInterval(() => { if (!cancelled) syncRoom(); }, 2500);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line
  }, [code]);

  // gameType 2 - 25-second snippet auto-stop
  useEffect(() => {
    if (room?.status === "in_round" && room?.gameType === 2 && room?.mode === "phone") {
      let remaining = 25;
      setSnippetTimer(remaining);
      const iv = setInterval(() => {
        remaining -= 1;
        setSnippetTimer(remaining);
        if (remaining <= 0) {
          clearInterval(iv);
          try { player?.pause?.(); } catch {}
        }
      }, 1000);
      return () => { clearInterval(iv); setSnippetTimer(null); };
    }
  }, [room?.status, room?.currentRound, room?.gameType, room?.mode]); // eslint-disable-line

  const players = useMemo(() => (room?.players || []).slice().sort((a, b) => b.score - a.score), [room?.players]);

  const activePlayer = (room?.players || []).find((p) => p.id === activePlayerId);
  const activeDisconnected = room?.status === "in_round" && room?.gameType === 1 && activePlayer && activePlayer.connected === false;
  const statText = (s) => {
    if (!s) return "";
    let txt = tr(`stat.${s.key}`);
    if (s.name != null) txt = txt.replace("{name}", s.name);
    if (s.year != null) txt = txt.replace("{year}", s.year);
    return `${s.emoji || ""} ${txt}`.trim();
  };

  const doReveal = async (override = null) => {
    try {
      await api.post(`/rooms/${code}/reveal`, { hostOverride: override });
    } catch {
      toast.error("Failed to reveal");
    }
  };

  const doNext = async () => {
    try {
      await api.post(`/rooms/${code}/next-round`);
    } catch {
      toast.error("Failed to advance");
    }
  };

  const doFinish = async () => {
    await api.post(`/rooms/${code}/finish`);
  };

  const doPause = async () => {
    const next = !paused;
    setPaused(next);
    try { await api.post(`/rooms/${code}/pause`, { paused: next }); } catch { setPaused(!next); }
  };

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [recapShown, setRecapShown] = useState(false);

  const doSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/rooms/${code}/save`, { name: saveName.trim() });
      toast.success(`Sessie opgeslagen: "${data.name}" ✓`);
      setShowSaveDialog(false);
      setSaveName("");
    } catch {
      toast.error("Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (!room) return <div className="min-h-screen flex items-center justify-center font-display text-navy">{tr("host.loading")}</div>;

  const isBoard = room.mode === "board";
  const currentVid = room.currentSong?.videoId;

  const hostTheme = getTheme(room.style || getStoredStyle());
  // Host background video: "afspeel" (in_round) + "resultaat" (reveal).
  const hostBgVideo = room.status === "in_round"
    ? hostTheme.hostGameVideo
    : (room.status === "reveal" ? hostTheme.hostRevealVideo
      : (room.status === "finished" ? hostTheme.hostFinishedVideo : null));

  return (
    <div className="min-h-screen host-bg overflow-x-hidden px-3 sm:px-8 pb-6" style={{ paddingTop: "10px", position: "relative", zIndex: 0, ...(hostBgVideo ? {} : bgImageStyle(room.status === "reveal" ? hostTheme.host.reveal : hostTheme.host.game)) }}>
      {hostBgVideo && (
        <VideoBg key={`host-${room.status}-${hostTheme.id}`} src={hostBgVideo} overlay={0.14} testid="host-play-bg-video" />
      )}
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-center mb-3">
          <HitmasterLogo className="h-32 sm:h-44 animate-float" />
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <BackToLobby />
          <div className="flex items-center gap-1.5 sm:gap-4">
            <div className="tactile-cream px-2 sm:px-3 py-1.5 sm:py-2">
              <div className="text-[10px] sm:text-xs font-black uppercase text-navy/70 leading-none">{tr("host.room")}</div>
              <div className="font-display font-black text-navy text-base sm:text-2xl tracking-widest leading-tight" data-testid="host-room-code">{code}</div>
            </div>
            <div className="tactile px-2 sm:px-3 py-1.5 sm:py-2">
              <div className="text-[10px] sm:text-xs font-black uppercase text-white leading-none" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}>{tr("host.round")}</div>
              <div className="font-display font-black text-white text-base sm:text-2xl leading-tight" data-testid="host-round" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}>{room.currentRound} / {room.totalRounds}</div>
            </div>
            <div className="tactile px-2 sm:px-3 py-1.5 sm:py-2">
              <div className="text-[10px] sm:text-xs font-black uppercase text-navy/70 leading-none">{tr("host.songs")}</div>
              <div className="font-display font-black text-navy text-base sm:text-2xl leading-tight" data-testid="host-songs-counter">
                {(room.playedVideoIds?.length ?? room.playedCount ?? 0)} / {room.totalSongs ?? "?"}
              </div>
            </div>
          </div>
        </div>

        {/* Center Stage */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 tactile p-5 min-h-[380px] flex flex-col items-center justify-center">
            {room.status === "reveal" && truth ? (
              <div className="flex flex-col items-center gap-4">
                <VinylReveal artwork={truth.artwork} artist={truth.artist} track={truth.track} year={truth.year} album={truth.collection} mode={room.gameType === 3 ? "g3" : "g1"} />
                {autoNextIn != null && autoNextIn > 0 && (
                  <div className="tactile-cream px-5 py-2 font-display font-black text-navy uppercase tracking-widest text-sm" data-testid="host-auto-next-countdown">
                    {paused ? tr("player.paused") : `${tr("player.nextSongIn")} ${autoNextIn}s…`}
                  </div>
                )}
                {roundStat && (
                  <div data-testid="host-round-stat" className="tactile-cream px-6 py-3 text-center max-w-md">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-navy/50">{tr("stat.title")}</div>
                    <div className="font-display font-black text-navy text-lg sm:text-xl leading-tight">{statText(roundStat)}</div>
                  </div>
                )}
              </div>
            ) : room.status === "finished" ? (
              <div className="w-full max-w-2xl flex flex-col items-center gap-3" data-testid="host-finished">
                <div className="font-display font-black text-navy text-4xl">{tr("host.gameOver")}</div>
                <div className="font-display text-navy text-2xl">{tr("player.winner")}: <span className="text-coral">{[...(room.players || [])].sort((a, b) => b.score - a.score)[0]?.name || "—"}</span></div>
                {!recapShown ? (
                  <button
                    data-testid="host-show-recap-btn"
                    onClick={() => setRecapShown(true)}
                    className="mt-2 bg-gold text-navy font-display font-black uppercase px-6 py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B]"
                  >
                    📜 Toon Game Recap
                  </button>
                ) : (
                  <>
                    <div className="w-full text-left" data-testid="host-recap-list">
                      <div className="font-bebas text-navy text-2xl tracking-wide mb-2 text-center">Game Recap</div>
                      <div className="max-h-[44vh] overflow-y-auto pr-1 flex flex-col gap-2">
                        {(room.history || []).map((h) => {
                          const scorers = (h.scorers || []).filter((s) => s.points > 0);
                          return (
                            <div key={h.round} className="rounded-xl border-2 border-navy bg-white/85 px-3 py-2" data-testid={`recap-round-${h.round}`}>
                              <div className="flex items-baseline justify-between gap-2">
                                <div className="font-display font-black text-navy truncate min-w-0">
                                  {h.round}. {h.title || "—"} <span className="text-navy/60 font-bold">· {h.artist || "—"}</span>
                                </div>
                                {h.year ? <span className="shrink-0 text-xs font-black bg-gold text-navy px-2 py-0.5 rounded-full border border-navy">{h.year}</span> : null}
                              </div>
                              <div className="text-xs font-bold text-navy/70 mt-1">
                                {scorers.length > 0
                                  ? scorers.map((s) => `${s.name} +${s.points} (${[s.artistOk && "artiest", s.trackOk && "titel", s.yearOk && "jaar"].filter(Boolean).join(", ") || "—"})`).join(" · ")
                                  : "Niemand scoorde deze ronde"}
                              </div>
                            </div>
                          );
                        })}
                        {(room.history || []).length === 0 && (
                          <div className="text-center text-navy/60 font-bold py-4">Geen rondes gespeeld</div>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid="host-restart-btn"
                      onClick={() => nav("/host")}
                      className="mt-2 bg-teal text-white font-display font-black uppercase px-6 py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B]"
                    >
                      Nieuw spel starten
                    </button>
                  </>
                )}
              </div>
            ) : room.status === "in_round" ? (
              <div className="w-full flex flex-col items-center gap-4">
                <div className="flex items-center gap-3">
                  <Equalizer /> <span className="font-display font-black text-navy uppercase tracking-widest">{tr("host.nowPlaying")}</span> <Equalizer />
                </div>
                <GameTypeBadge gameType={room.gameType} size="lg" />
                {activeDisconnected && (
                  <div
                    data-testid="host-waiting-player"
                    className="w-full max-w-md rounded-2xl px-5 py-3 text-center font-display font-black uppercase tracking-widest bg-coral text-white border-[3px] border-navy animate-pulse"
                    style={{ textShadow: "0 2px 6px rgba(0,0,0,0.5)" }}
                  >
                    ⏳ {tr("host.waitingFor").replace("{name}", activePlayer?.name || "—")}
                  </div>
                )}
                {room.gameType === 1 && room.mode !== "board" && activePlayerId && (
                  <div
                    data-testid="host-current-turn-banner"
                    className="w-full max-w-md rounded-2xl px-5 py-3 text-center font-display font-black uppercase tracking-widest"
                    style={{
                      backgroundColor: playerColor(room.players, activePlayerId),
                      color: "#FFF9EE",
                      border: "3px solid #FFF9EE",
                      textShadow: "0 2px 6px rgba(0,0,0,0.5)",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
                    }}
                  >
                    <div className="text-xs opacity-80 leading-none mb-1">{tr("player.currentTurn")}</div>
                    <div className="text-2xl sm:text-3xl leading-none" data-testid="host-current-turn-name">
                      {(room.players || []).find((p) => p.id === activePlayerId)?.name || "—"}
                    </div>
                  </div>
                )}
                {room.gameType === 1 && challengeInfo && (
                  <div
                    data-testid="host-challenge-window"
                    className="w-full max-w-md rounded-2xl px-6 py-4 text-center bg-navy border-[3px] border-cream shadow-[0_10px_0_rgba(0,0,0,0.35)]"
                  >
                    <div className="font-bebas text-coral uppercase tracking-widest text-lg leading-none mb-2">
                      {tr("host.challengeNow")}
                    </div>
                    {challengeInfo.afterPass ? (
                      <div className="font-bebas text-cream text-2xl sm:text-3xl leading-tight" data-testid="host-challenge-guess">
                        {tr("player.passedKnowsNothing").replace("{name}", (room.players || []).find((p) => p.id === challengeInfo.playerId)?.name || "—")}
                      </div>
                    ) : (
                      <>
                        <div className="font-bebas text-cream/70 uppercase tracking-widest text-sm leading-none">
                          {(room.players || []).find((p) => p.id === challengeInfo.playerId)?.name || "—"} {tr("player.says")}
                        </div>
                        <div className="font-display font-black text-cream text-xl sm:text-2xl leading-tight mt-1" data-testid="host-challenge-guess">
                          {challengeInfo.artist || "—"} <span className="opacity-50">-</span> {challengeInfo.track || "—"}
                          {challengeInfo.year != null && challengeInfo.year !== "" ? ` (${challengeInfo.year})` : ""}
                        </div>
                      </>
                    )}
                    {challengeLeft != null && (
                      <div
                        className="font-display font-black text-cream tabular-nums leading-none mt-2"
                        style={{ fontSize: "clamp(2.5rem, 8vw, 4rem)" }}
                        data-testid="host-challenge-countdown"
                      >
                        {challengeLeft}
                      </div>
                    )}
                  </div>
                )}
                {currentVid ? (
                  <>
                    {room.gameType === 3 ? (
                      <MosaicReveal
                        artwork={mosaicArtwork}
                        order={mosaicOrder}
                        frozen={!!buzzerPlayerId}
                        intervalMs={500}
                        size="min(60vw, 50vh, 380px)"
                      />
                    ) : room.gameType === 4 ? (
                      <div
                        data-testid="host-g4-placeholder"
                        className="w-full max-w-md rounded-2xl border-4 border-navy bg-gradient-to-br from-teal to-navy p-6 flex flex-col items-center gap-3"
                        style={{ minHeight: "220px" }}
                      >
                        <div className="text-6xl">📱🎬</div>
                        <div className="font-bebas text-cream text-3xl tracking-widest text-center leading-tight">
                          {tr("host.videoOnPhones")}<br/>{tr("host.phonesLine")}
                        </div>
                        <div className="font-poppins text-cream/80 text-sm text-center max-w-xs">
                          {tr("host.videoOnPhonesDesc")}
                        </div>
                      </div>
                    ) : room.audioSource === "players" ? (
                      <div
                        data-testid="host-players-audio-placeholder"
                        className="w-full max-w-md rounded-2xl border-4 border-navy bg-gradient-to-br from-teal to-navy p-6 flex flex-col items-center gap-3"
                        style={{ minHeight: "220px" }}
                      >
                        <div className="text-6xl animate-pulse">📱🔊</div>
                        <div className="font-bebas text-cream text-3xl tracking-widest text-center leading-tight">
                          {tr("host.musicOnPhones")}<br/>{tr("host.phonesLine")}
                        </div>
                        <div className="font-poppins text-cream/80 text-sm text-center max-w-xs">
                          {tr("host.musicOnPhonesDesc")}
                        </div>
                      </div>
                    ) : (
                      <div className="w-56 h-56 sm:w-64 sm:h-64 relative">
                        <div className="vinyl animate-spin-slow" />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <Equalizer active={!needsUnlock} />
                        </div>
                      </div>
                    )}
                    {needsUnlock && room.gameType !== 4 && room.audioSource !== "players" && (
                      <button
                        data-testid="host-unlock-audio-btn"
                        onClick={unlockAndPlay}
                        className="bg-coral text-white font-bebas text-2xl uppercase tracking-widest px-8 py-4 rounded-2xl border-4 border-navy shadow-[0_8px_0_#7f1d1d] active:translate-y-[8px] active:shadow-none transition-all"
                      >
                        🔊 {tr("host.tapToPlay").replace("🔊 ", "")}
                      </button>
                    )}
                  </>
                ) : null}
                {room.gameType === 2 && snippetTimer != null && (
                  <div className="mt-2 font-display font-black text-coral text-4xl" data-testid="snippet-timer">{Math.max(0, snippetTimer)}s</div>
                )}
                {buzzerPlayerId && (
                  <div className="font-display font-black text-navy text-xl" data-testid="host-buzzer">
                    {tr("host.buzzed")} <span className="text-coral">{players.find((p) => p.id === buzzerPlayerId)?.name || buzzerPlayerId}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <div className="font-display font-black text-navy text-3xl">{tr("host.waitingStart")}</div>
                <div className="text-navy/60 font-bold">{tr("host.configHint")}</div>
              </div>
            )}
          </div>

          {/* Right column - scoreboard + controls */}
          <div className="space-y-3">
            <div className="tactile-cream p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bebas text-navy text-2xl tracking-wide">{tr("host.scoreboard")}</h3>
                <div className="text-xs font-bold uppercase text-navy/60">{players.length}/8</div>
              </div>
              <div className="space-y-2">
                {players.length === 0 ? (
                  <div className="text-navy/60 font-bold text-sm">{tr("host.noPlayers")}</div>
                ) : players.map((p, i) => (
                  <PlayerScoreCard key={p.id} player={p} rank={i + 1} buzzed={p.id === buzzerPlayerId} color={playerColor(room.players, p.id)} />
                ))}
              </div>
            </div>

            <div className="tactile p-4 space-y-2">
              <h3 className="font-bebas text-navy text-2xl tracking-wide">{tr("host.controls")}</h3>
              {room.status === "in_round" && (
                <>
                  <button data-testid="host-reveal-btn" onClick={() => doReveal(null)} className="w-full bg-gold text-navy font-display font-black uppercase py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B] hover:translate-y-[-2px] transition-transform">
                    {tr("host.revealAnswer")}
                  </button>
                  {isBoard && (
                    <div className="text-xs font-bold text-navy/60 text-center">{tr("host.boardModeNote")}</div>
                  )}
                </>
              )}
              {room.status === "reveal" && room.currentRound < room.totalRounds && (
                <button data-testid="host-next-btn" onClick={doNext} className="w-full bg-teal text-white font-display font-black uppercase py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B]">
                  {tr("host.nextRound")}
                </button>
              )}
              {room.status === "reveal" && room.currentRound < room.totalRounds && (
                <button
                  data-testid="host-pause-btn"
                  onClick={doPause}
                  className={`w-full font-display font-black uppercase py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B] ${paused ? "bg-gold text-navy" : "bg-navy text-cream"}`}
                >
                  {paused ? "▶ Hervat" : "⏸ Pauzeer volgende"}
                </button>
              )}
              {room.status === "reveal" && room.currentRound >= room.totalRounds && (
                <button data-testid="host-finish-btn" onClick={doFinish} className="w-full bg-coral text-white font-display font-black uppercase py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B]">
                  {tr("host.finishGame")}
                </button>
              )}
              <button
                data-testid="host-save-session-btn"
                onClick={() => { setSaveName(""); setShowSaveDialog(true); }}
                className="w-full bg-gold text-navy font-display font-black uppercase py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B] flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" strokeWidth={2.6} /> Sessie opslaan
              </button>
              <button data-testid="host-back-btn" onClick={async () => { try { await doFinish(); } catch {} nav("/"); }} className="w-full font-bold text-navy/60 uppercase text-xs py-2 hover:text-navy">{tr("host.exitHome")}</button>

              {/* Reveal verdicts + host override */}
              {room.status === "reveal" && Object.keys(verdicts).length > 0 && (
                <div className="mt-3 border-t-2 border-navy/20 pt-3">
                  <div className="font-display font-black text-navy text-sm uppercase mb-1">{tr("host.answers")}</div>
                  <div className="space-y-1.5 max-h-56 overflow-auto">
                    {players.map((p) => {
                      const v = verdicts[p.id];
                      if (!v) return null;
                      const col = playerColor(room.players, p.id);
                      const b = v.breakdown;
                      const md = v.mode || "g1";
                      let parts = [];
                      if (b) {
                        if (md === "g3") parts = [[tr("player.artistColon"), b.artist], [tr("player.albumColon"), b.album]];
                        else if (md === "g2") parts = [[tr("player.artistColon"), b.artist], [tr("player.titleColon"), b.track]];
                        else parts = [[tr("player.artistColon"), b.artist], [tr("player.titleColon"), b.track], [tr("player.yearColon"), b.year]];
                        if (b.bonus > 0) parts.push([tr("player.brkBonus"), b.bonus]);
                      }
                      return (
                        <div
                          key={p.id}
                          data-testid={`host-verdict-${p.id}`}
                          className="rounded-lg bg-white/55 px-2.5 py-1.5"
                          style={{ borderLeft: `5px solid ${col}` }}
                        >
                          <div className="flex justify-between items-center text-sm font-bold">
                            <span className="text-navy flex items-center gap-1.5 min-w-0">
                              <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: col }} />
                              <span className="truncate">{p.name}</span>
                            </span>
                            <span className={v.points > 0 ? "text-teal" : "text-coral"}>+{v.points}{v.yearOk ? " ⭐" : ""}</span>
                          </div>
                          {parts.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-x-2.5 gap-y-0.5 text-[11px] font-semibold" data-testid={`host-verdict-breakdown-${p.id}`}>
                              {parts.map(([label, pts]) => (
                                <span key={label} className={(pts || 0) > 0 ? "text-navy/80" : "text-navy/35"}>
                                  {label}: {pts || 0}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="text-center text-xs font-bold text-navy/40 uppercase tracking-widest">WS: {status}</div>
          </div>
        </div>
      </div>
      {/* G3 custom cinematic clip - hidden audio element, only heard when G3 round starts */}
      <audio
        ref={customAudioRef}
        src={CINEMATIC_AUDIO_URL}
        preload="auto"
        data-testid="host-g3-audio"
        style={{ display: "none" }}
      />
      {showSaveDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: "rgba(6,10,24,0.72)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
          data-testid="save-session-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSaveDialog(false); }}
        >
          <div className="w-full max-w-sm bg-navy border-4 border-gold rounded-3xl p-6 flex flex-col gap-4 shadow-[0_16px_40px_rgba(0,0,0,0.5)]">
            <div className="font-bebas text-white text-white-shadow text-3xl tracking-widest uppercase text-center flex items-center justify-center gap-2">
              <Save className="w-7 h-7 text-gold" strokeWidth={2.6} /> Sessie opslaan
            </div>
            <p className="text-center text-cream/80 text-sm font-bold -mt-1">
              Geef deze sessie een naam. Je kunt hem later op elk apparaat hervatten.
            </p>
            <input
              data-testid="save-session-name-input"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              maxLength={40}
              autoFocus
              placeholder="Bijv. Vrijdagavond feestje"
              onKeyDown={(e) => { if (e.key === "Enter" && !saving) doSave(); }}
              className="w-full rounded-xl border-2 border-navy px-4 py-3 font-display font-bold text-navy text-lg focus:outline-none focus:ring-4 focus:ring-gold/50"
            />
            <div className="text-center text-cream/60 text-xs font-bold uppercase tracking-widest">
              Ronde {room.currentRound}/{room.totalRounds} · {(room.playedVideoIds?.length ?? 0)}/{room.totalSongs ?? "?"} nummers
            </div>
            <div className="flex gap-3">
              <button
                data-testid="save-session-cancel-btn"
                onClick={() => setShowSaveDialog(false)}
                className="flex-1 bg-navy text-cream/80 font-display font-black uppercase py-3 rounded-xl border-2 border-cream/30"
              >
                Annuleren
              </button>
              <button
                data-testid="save-session-confirm-btn"
                onClick={doSave}
                disabled={saving}
                className="flex-1 bg-gold text-navy font-display font-black uppercase py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#1E1B4B] disabled:opacity-60"
              >
                {saving ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
